/**
 * Material Taxonomy API Layer
 * Base route: /api/v1/Taxonomy/PackagingMaterials
 * Handles all REST endpoints for Class, Category, and FunctionalUnit
 */

import http from '../../../../http'
import { API_URL } from '../../../../const'
import { IClassCreateDTO, ICategoryCreateDTO, IFunctionalUnitCreateDTO, IVerifyDTO } from './types'

const BASE_URL = `${API_URL}/Taxonomy/PackagingMaterials`

// ===========================
// Class Endpoints (Level 1)
// ===========================

/**
 * Get all classes (lite)
 */
export const getClasses = () => http.get(`${BASE_URL}/class`)

/**
 * Get class by ID
 */
export const getClass = (id: string) => http.get(`${BASE_URL}/class/${id}`)

/**
 * Create new class
 */
export const createClass = (data: IClassCreateDTO) => http.post(`${BASE_URL}/class`, { data })

/**
 * Update existing class
 */
export const updateClass = (id: string, data: IClassCreateDTO) =>
  http.put(`${BASE_URL}/class/${id}`, { data })

/**
 * Publish class
 */
export const publishClass = (id: string) => http.post(`${BASE_URL}/class/publish/${id}`)

/**
 * Unpublish class
 */
export const unpublishClass = (id: string) => http.delete(`${BASE_URL}/class/unpublish/${id}`)

/**
 * Get class audit trail
 */
export const getClassAudit = (id: string) => http.get(`${BASE_URL}/class/audit/${id}`)

/**
 * Get published classes only
 */
export const getPublishedClasses = () => http.get(`${BASE_URL}/published/class`)

// ===========================
// Category Endpoints (Level 2)
// ===========================

/**
 * Get categories by class ID (lite)
 */
export const getCategories = (classId: number) =>
  http.get(`${BASE_URL}/category?classId=${classId}`)

/**
 * Get category by ID
 */
export const getCategory = (id: string) => http.get(`${BASE_URL}/category/${id}`)

/**
 * Create new category
 */
export const createCategory = (data: ICategoryCreateDTO) =>
  http.post(`${BASE_URL}/category`, { data })

/**
 * Update existing category
 */
export const updateCategory = (id: string, data: ICategoryCreateDTO) =>
  http.put(`${BASE_URL}/category/${id}`, { data })

/**
 * Publish category
 * Note: Parent class must be published
 */
export const publishCategory = (id: string) => http.post(`${BASE_URL}/category/publish/${id}`)

/**
 * Unpublish category
 */
export const unpublishCategory = (id: string) => http.delete(`${BASE_URL}/category/unpublish/${id}`)

/**
 * Get category audit trail
 */
export const getCategoryAudit = (id: string) => http.get(`${BASE_URL}/category/audit/${id}`)

/**
 * Get published categories by class
 */
export const getPublishedCategories = (classId: number) =>
  http.get(`${BASE_URL}/published/category?classId=${classId}`)

// ===========================
// Functional Unit Endpoints (Level 3)
// ===========================

/**
 * Get functional units by category ID (lite)
 */
export const getFunctionalUnits = (categoryId: number) =>
  http.get(`${BASE_URL}/functionalUnit?categoryId=${categoryId}`)

/**
 * Get functional unit by ID
 */
export const getFunctionalUnit = (id: string) => http.get(`${BASE_URL}/functionalUnit/${id}`)

/**
 * Create new functional unit
 */
export const createFunctionalUnit = (data: IFunctionalUnitCreateDTO) =>
  http.post(`${BASE_URL}/functionalUnit`, { data })

/**
 * Update existing functional unit
 * Note: Researcher fields are preserved from original creation
 */
export const updateFunctionalUnit = (id: string, data: IFunctionalUnitCreateDTO) =>
  http.put(`${BASE_URL}/functionalUnit/${id}`, { data })

/**
 * Publish functional unit
 * Note: Case difference in endpoint - 'functionalunit' not 'functionalUnit'
 * Note: Parent category must be published
 */
export const publishFunctionalUnit = (id: string) =>
  http.post(`${BASE_URL}/functionalunit/publish/${id}`)

/**
 * Unpublish functional unit
 * Note: Case difference in endpoint - 'functionalunit' not 'functionalUnit'
 */
export const unpublishFunctionalUnit = (id: string) =>
  http.delete(`${BASE_URL}/functionalunit/unpublish/${id}`)

/**
 * Verify functional unit
 * Sets verifiedByName, verifiedByEmail, verifiedOn from authenticated user
 */
export const verifyFunctionalUnit = (id: string, data: IVerifyDTO) =>
  http.put(`${BASE_URL}/functionalunit/verify/${id}`, { data })

/**
 * Get functional unit configuration
 * For new records, use id = '0' to get empty path with enum dropdowns
 * For existing records, returns full path + enum dropdowns
 */
export const getFunctionalUnitConfiguration = (id: string) =>
  http.get(`${BASE_URL}/functionalunit/configuration/${id}`)

/**
 * Get functional unit audit trail
 */
export const getFunctionalUnitAudit = (id: string) =>
  http.get(`${BASE_URL}/functionalunit/audit/${id}`)

/**
 * Get published functional units by category
 */
export const getPublishedFunctionalUnits = (categoryId: number) =>
  http.get(`${BASE_URL}/published/functionalUnit?categoryId=${categoryId}`)

// ===========================
// Utility Functions
// ===========================

/**
 * Generic publish path function for any hierarchy level
 * @param path - 'class', 'category', or 'functionalunit'
 * @param id - Entity ID
 */
export const publishPath = (path: string, id: string) =>
  http.post(`${BASE_URL}/${path}/publish/${id}`)

/**
 * Generic unpublish path function for any hierarchy level
 * @param path - 'class', 'category', or 'functionalunit'
 * @param id - Entity ID
 */
export const unpublishPath = (path: string, id: string) =>
  http.delete(`${BASE_URL}/${path}/unpublish/${id}`)
